<template>
  <v-card ref="notes" :loading="notesLoading" class="notes">
    
    <v-card-title>
      Notes      
      <template v-if="!notesLoading">
        <v-spacer></v-spacer>
        <v-btn color="secondary" x-small @click="openAddNoteDialog()"><v-icon small>add</v-icon> Add note</v-btn>

        <slot name="additional-buttons"></slot>
      </template>
    </v-card-title>

    <template v-if="notesLoading">
      <v-card-text>
        <v-skeleton-loader type="article, article"></v-skeleton-loader>
      </v-card-text>
    </template>

    <template v-if="notesError">
      <v-card-text class="error--text">There was an error loading the notes api.</v-card-text>
    </template>

    <template v-else>
      <div class="notes-wrapper">
        <v-card-text class="notes-text">
          <v-list dense three-line>

            <template v-for="(note, index) in notes">
              <v-card :key="index" width="100%" class="mb-4">
                <v-list-item class="elevate-2 text-left" @click="openViewNoteDialog(note)"> 
                  <v-list-item-content>
                    <v-list-item-title>{{ note.user | username }} <span class="body--text">{{note.type ? ` -  ${note.type}` : ''}}</span></v-list-item-title>
                    <v-list-item-subtitle :class="formatNote ? 'notes__note-message' : ''">
                      <span v-html="(noteMessage => formatNote ? formatNoteMessage(noteMessage) : noteMessage)(note.note)"></span>
                    </v-list-item-subtitle>
                  </v-list-item-content>      

                  <v-list-item-action>
                    <v-list-item-action-text>{{note.creation_timestamp | formatDate}}</v-list-item-action-text>
                  </v-list-item-action>      
                </v-list-item>
              </v-card>
            </template>

          </v-list>
        </v-card-text>
      </div>

      <ViewNoteDialog :open="viewNoteOpen" :selectedNote="selectedNote" @closeDialog="viewNoteOpen=false" />
      <AddNoteDialog :open="addNoteOpen" :id="id" :notesType="notesType" @closeDialog="addNoteOpen=false" />
    </template>

  </v-card> 
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex';
  import ViewNoteDialog from '@/components/Globals/Notes/ViewNoteDialog';
  import AddNoteDialog from '@/components/Globals/Notes/AddNoteDialog';
  
  export default {
    components: { ViewNoteDialog, AddNoteDialog },

    props: {
      id: String,
      notesType: String,
      formatNote: {
        type: Boolean,
        default: false,
      },
    },
    
    data() {
      return {
        notesLoading: true,
        notesError: false,
        viewNoteOpen: false,
        selectedNote: {},
        addNoteOpen: false,
      }
    },

    computed: {
      ...mapState('accounts', ['currentAccountNotes']),
      ...mapGetters('accounts', ['getCurrentAccountNotesError']),
      ...mapGetters('productNotes', ['getProductNotes', 'getProductNotesErr']),
      ...mapGetters('tickets',['getTicketNotes', 'getCurrentTicketNotesError']),
      ...mapGetters('retention', ['getRetentionLeadNotes', 'getFetchRetentionLeadNotesError']),

      notes() {
        switch(this.notesType) {
          case 'accounts':
            return this.currentAccountNotes;
          case 'product': 
            return this.getProductNotes;
          case 'ticket':
            return this.getTicketNotes;
          case 'retention':
            return this.getRetentionLeadNotes;
        }
      },
    },

    async mounted() {
      switch(this.notesType) {
        case 'accounts':
          await this.getAccountNotes(this.id);
          this.notesError = this.getCurrentAccountNotesError;
          break;
        case 'product': 
          await this.loadProductNotes(this.id);
          await this.fetchNoteTemplates();
          this.notesError = this.getProductNotesErr;
          break;
        case 'ticket':
          await this.fetchTicketNotesByID(this.id);
          this.notesError = this.getCurrentTicketNotesError;       
          break;
        case 'retention':
          await this.fetchRetentionLeadNotes(this.id);
          this.notesError = this.getFetchRetentionLeadNotesError;
          break;    
      }
      this.notesLoading = false;
    },

    methods: {
      ...mapActions('accounts', ['getAccountNotes']),
      ...mapActions('productNotes', ['loadProductNotes', 'fetchNoteTemplates']),
      ...mapActions('tickets', ['fetchTicketNotesByID']),
      ...mapActions('retention', ['fetchRetentionLeadNotes']),

      formatNoteMessage(str, is_xhtml) {
        if (typeof str === 'undefined' || str === null) {
          return ''; 
        }
        
        const breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';
        return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
      },

      openAddNoteDialog() {
        this.addNoteOpen = true;
      },

      openViewNoteDialog(note) {
        this.viewNoteOpen = true;
        this.selectedNote = note;
      }
    },

    filters: {
      username(username) {
        return username ? username.toLowerCase() : 'unassigned';
      }
    },   
  }
</script>

<style lang="scss" scoped>
.notes__note-message {
  -webkit-line-clamp: initial !important;
}
.notes {
  height: 100%;
  min-height: 300px;
  overflow-y: scroll;
  
  ::v-deep {
    .v-card__title {
      top: 0px;
      position: sticky;
      z-index: 2;
      background-color: white;
    }
  }
  .notes-wrapper {
    position: relative;
    .notes-text {
      position: absolute;
    }
  }
}
</style>